import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Badge } from '@/shared/ui/badge';
import { AppDetailLayout } from '@/shared/app-detail-layout';
import {
  executionStatusLabel,
  executionStatusTone,
  requiredActionLabel,
} from '@/modules/agreements/shared';
import { buildSigningSections } from './parts/signing-sections';
import { useSigningExecution } from './hooks/use-signing-execution';
import { useSigning } from './hooks/use-signing';
import type { AgreementExecutionResponse } from '@/api/schemas/agreements.types';

function signingBadges(execution: AgreementExecutionResponse): ReactNode[] {
  return [
    <Badge key="action" tone="info">
      {requiredActionLabel(execution.requiredAction)}
    </Badge>,
  ];
}

/**
 * Review and sign one agreement execution.
 *
 * The flow is staged and each stage is a separate server call: review the content, capture a
 * signature image, complete a fresh identity check when the requirement demands one, then
 * sign. No stage completes the execution on its own.
 */
export function SigningPage(props: { executionId: string }): ReactNode {
  const navigate = useNavigate();
  const { execution, error, isLoading, reload, isDenied } = useSigningExecution(
    props.executionId,
  );
  const signing = useSigning(props.executionId, execution);

  return (
    <AppDetailLayout
      headerVariant="accent"
      title="Review and sign"
      subtitle={execution ? `Execution ${execution.executionRef}` : undefined}
      breadcrumbs={[{ label: 'Agreements', href: '/agreements' }, { label: 'Sign' }]}
      isLoading={isLoading}
      isDenied={isDenied}
      error={error}
      status={
        execution
          ? {
              label: executionStatusLabel(execution.status),
              tone: executionStatusTone(execution.status),
            }
          : undefined
      }
      badges={execution ? signingBadges(execution) : undefined}
      sections={
        execution
          ? buildSigningSections({
              executionId: props.executionId,
              contentHash: execution.contentHash,
              signing,
              onSigned: () => void signing.sign(reload),
              onBack: () => {
                void navigate({ to: '/agreements' });
              },
            })
          : []
      }
    />
  );
}
